import React, { useState } from 'react';
import { 
  Plus, 
  Search,
  Edit2, 
  Trash2, 
  Eye,
  X,
  Save,
  Copy,
  ArrowUp,
  ArrowDown,
  FileText,
  Mail,
  Hash,
  Calendar,
  AlignLeft,
  List,
  CheckSquare,
  Upload,
  ToggleLeft
} from 'lucide-react';

const CustomizableForm = () => {
  const [forms, setForms] = useState([
    { id: 1, name: 'Application Form', fields: 3, status: 'active', lastModified: '2024-11-15', responses: 45 },
    { id: 2, name: 'Feedback Form', fields: 5, status: 'active', lastModified: '2024-11-12', responses: 28 },
    { id: 3, name: 'Registration Form', fields: 8, status: 'draft', lastModified: '2024-11-10', responses: 0 },
  ]);

  const [selectedForm, setSelectedForm] = useState(null);
  const [formFields, setFormFields] = useState([]);
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [showFormList, setShowFormList] = useState(true);
  const [currentField, setCurrentField] = useState({
    id: null,
    label: '',
    type: 'text',
    required: false,
    options: '',
    placeholder: '',
    helpText: ''
  });

  const fieldTypes = [
    { value: 'text', label: 'Text Input', icon: FileText },
    { value: 'email', label: 'Email', icon: Mail },
    { value: 'number', label: 'Number', icon: Hash },
    { value: 'date', label: 'Date', icon: Calendar },
    { value: 'textarea', label: 'Text Area', icon: AlignLeft },
    { value: 'select', label: 'Dropdown', icon: List },
    { value: 'radio', label: 'Radio Buttons', icon: CheckSquare },
    { value: 'checkbox', label: 'Checkboxes', icon: CheckSquare },
    { value: 'file', label: 'File Upload', icon: Upload },
    { value: 'toggle', label: 'Toggle Switch', icon: ToggleLeft }
  ];

  const handleSelectForm = (form) => {
    setSelectedForm(form);
    // Load form fields from the selected form
    setFormFields(generateDemoFields(form.fields));
    setShowFormList(false);
  };

  const generateDemoFields = (count) => {
    const demoFields = [
      { id: 1, label: 'Full Name', type: 'text', required: true, placeholder: 'Enter your full name', helpText: '' },
      { id: 2, label: 'Email Address', type: 'email', required: true, placeholder: 'your@email.com', helpText: '' },
      { id: 3, label: 'Phone Number', type: 'number', required: false, placeholder: '1234567890', helpText: '' },
      { id: 4, label: 'Date of Birth', type: 'date', required: true, placeholder: '', helpText: '' },
      { id: 5, label: 'Comments', type: 'textarea', required: false, placeholder: 'Enter your comments', helpText: 'Max 500 characters' },
      { id: 6, label: 'Country', type: 'select', required: true, options: 'India,USA,UK,Canada', placeholder: '', helpText: '' },
      { id: 7, label: 'Gender', type: 'radio', required: true, options: 'Male,Female,Other', placeholder: '', helpText: '' },
      { id: 8, label: 'Interests', type: 'checkbox', required: false, options: 'Sports,Music,Reading,Travel', placeholder: '', helpText: '' },
    ];
    return demoFields.slice(0, count);
  };

  const handleAddField = () => {
    setCurrentField({
      id: null,
      label: '',
      type: 'text',
      required: false,
      options: '',
      placeholder: '',
      helpText: ''
    });
    setShowFieldModal(true);
  };

  const handleEditField = (field) => {
    setCurrentField({ ...field });
    setShowFieldModal(true);
  };

  const handleSaveField = () => {
    if (!currentField.label) {
      alert('Please enter a field label');
      return;
    }

    if (currentField.id) {
      setFormFields(formFields.map(f => f.id === currentField.id ? currentField : f));
    } else {
      setFormFields([...formFields, { ...currentField, id: Date.now() }]);
    }
    setShowFieldModal(false);
  };

  const handleDeleteField = (fieldId) => {
    if (window.confirm('Are you sure you want to delete this field?')) {
      setFormFields(formFields.filter(f => f.id !== fieldId));
    }
  };

  const moveField = (index, direction) => {
    const newFields = [...formFields];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex >= 0 && targetIndex < newFields.length) {
      [newFields[index], newFields[targetIndex]] = [newFields[targetIndex], newFields[index]];
      setFormFields(newFields);
    }
  };

  const getFieldIcon = (type) => {
    const fieldType = fieldTypes.find(ft => ft.value === type);
    return fieldType ? fieldType.icon : FileText;
  };

  const renderFieldPreview = (field) => {
    const Icon = getFieldIcon(field.type);
    
    switch (field.type) {
      case 'textarea':
        return (
          <textarea 
            placeholder={field.placeholder} 
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3d82c4] focus:border-transparent resize-none" 
            rows="3"
            disabled 
          />
        );
      case 'select':
        return (
          <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3d82c4] focus:border-transparent" disabled>
            <option>Select an option</option>
            {field.options.split(',').map((opt, idx) => (
              <option key={idx}>{opt.trim()}</option>
            ))}
          </select>
        );
      case 'radio':
        return (
          <div className="space-y-2">
            {field.options.split(',').map((opt, idx) => (
              <label key={idx} className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name={field.label} className="text-[#3d82c4]" disabled />
                <span className="text-sm text-gray-700">{opt.trim()}</span>
              </label>
            ))}
          </div>
        );
      case 'checkbox':
        return (
          <div className="space-y-2">
            {field.options.split(',').map((opt, idx) => (
              <label key={idx} className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="text-[#3d82c4] rounded" disabled />
                <span className="text-sm text-gray-700">{opt.trim()}</span>
              </label>
            ))}
          </div>
        );
      case 'file':
        return (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
            <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600">Click to upload or drag and drop</p>
          </div>
        );
      case 'toggle':
        return (
          <label className="flex items-center gap-3 cursor-pointer">
            <div className="relative">
              <input type="checkbox" className="sr-only" disabled />
              <div className="w-11 h-6 bg-gray-300 rounded-full"></div>
              <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition"></div>
            </div>
            <span className="text-sm text-gray-700">Enable this option</span>
          </label>
        );
      default:
        return (
          <input 
            type={field.type} 
            placeholder={field.placeholder}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3d82c4] focus:border-transparent" 
            disabled 
          />
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Customizable Form Builder</h1>
          <p className="text-gray-600 mt-1">Create and manage dynamic forms with ease</p>
        </div>
        <div className="flex gap-3">
          {selectedForm && (
            <button
              onClick={() => {
                setShowFormList(true);
                setSelectedForm(null);
              }}
              className="px-4 py-2.5 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg font-medium transition-colors"
            >
              Back to Forms
            </button>
          )}
          <button className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#3d82c4] hover:bg-[#2d6fad] text-white rounded-lg font-medium transition-colors shadow-sm">
            <Plus className="w-5 h-5" />
            New Form
          </button>
        </div>
      </div>

      {showFormList ? (
        /* Forms List View */
        <div className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <p className="text-sm text-gray-600 font-medium">Total Forms</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">{forms.length}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <p className="text-sm text-gray-600 font-medium">Active Forms</p>
              <p className="text-3xl font-bold text-green-600 mt-2">{forms.filter(f => f.status === 'active').length}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <p className="text-sm text-gray-600 font-medium">Total Responses</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">{forms.reduce((sum, f) => sum + f.responses, 0)}</p>
            </div>
          </div>

          {/* Forms Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {forms.map(form => (
              <div key={form.id} className="bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-all cursor-pointer" onClick={() => handleSelectForm(form)}>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                        <FileText className="w-6 h-6 text-[#3d82c4]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800">{form.name}</h3>
                        <p className="text-xs text-gray-500">{form.fields} fields</p>
                      </div>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${form.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {form.status}
                    </span>
                  </div>
                  
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex justify-between">
                      <span>Responses</span>
                      <span className="font-semibold">{form.responses}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Last Modified</span>
                      <span className="font-semibold">{form.lastModified}</span>
                    </div>
                  </div>

                  <div className="flex gap-2 mt-4 pt-4 border-t border-gray-100">
                    <button className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors text-sm font-medium">
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </button>
                    <button className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-50 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors text-sm font-medium">
                      <Eye className="w-4 h-4" />
                      Preview
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        /* Form Builder View */
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Field Types Palette */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg border border-gray-200 p-5 sticky top-6">
              <h3 className="font-semibold text-gray-800 mb-4">Field Types</h3>
              <div className="space-y-2">
                {fieldTypes.map(fieldType => {
                  const Icon = fieldType.icon;
                  return (
                    <button
                      key={fieldType.value}
                      onClick={() => {
                        setCurrentField({
                          id: null,
                          label: '',
                          type: fieldType.value,
                          required: false,
                          options: '',
                          placeholder: '',
                          helpText: ''
                        });
                        setShowFieldModal(true);
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-[#3d82c4] transition-all text-left"
                    >
                      <Icon className="w-5 h-5 text-gray-600" />
                      <span className="text-sm font-medium text-gray-700">{fieldType.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Form Canvas */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">{selectedForm?.name}</h3>
                  <p className="text-sm text-gray-500">{formFields.length} fields</p>
                </div>
                <div className="flex gap-2">
                  <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors text-sm font-medium">
                    <Eye className="w-4 h-4" />
                    Preview
                  </button>
                  <button className="flex items-center gap-2 px-4 py-2 bg-[#3d82c4] hover:bg-[#2d6fad] text-white rounded-lg transition-colors text-sm font-medium">
                    <Save className="w-4 h-4" />
                    Save Form
                  </button>
                </div>
              </div>

              <div className="p-6">
                {formFields.length === 0 ? (
                  <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                    <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">No fields added yet</h3>
                    <p className="text-gray-600 mb-6">Click on a field type to start building your form</p>
                    <button
                      onClick={handleAddField}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#3d82c4] hover:bg-[#2d6fad] text-white rounded-lg font-medium transition-colors"
                    >
                      <Plus className="w-5 h-5" />
                      Add First Field
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {formFields.map((field, index) => {
                      const Icon = getFieldIcon(field.type);
                      return (
                        <div key={field.id} className="border border-gray-200 rounded-lg p-5 hover:border-[#3d82c4] transition-colors">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3 flex-1">
                              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                                <Icon className="w-5 h-5 text-gray-600" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <label className="text-sm font-semibold text-gray-800">
                                    {field.label}
                                  </label>
                                  {field.required && (
                                    <span className="text-red-500 text-sm">*</span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-500 capitalize">{field.type}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => moveField(index, 'up')}
                                disabled={index === 0}
                                className="p-1.5 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                              >
                                <ArrowUp className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => moveField(index, 'down')}
                                disabled={index === formFields.length - 1}
                                className="p-1.5 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                              >
                                <ArrowDown className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleEditField(field)}
                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteField(field.id)}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <div className="mt-3">
                            {renderFieldPreview(field)}
                            {field.helpText && (
                              <p className="text-xs text-gray-500 mt-2">{field.helpText}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Field Edit Modal */}
      {showFieldModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-800">
                {currentField.id ? 'Edit Field' : 'Add New Field'}
              </h3>
              <button
                onClick={() => setShowFieldModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Field Label */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Field Label *
                </label>
                <input
                  type="text"
                  value={currentField.label}
                  onChange={(e) => setCurrentField({...currentField, label: e.target.value})}
                  placeholder="Enter field label"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3d82c4] focus:border-transparent"
                />
              </div>

              {/* Field Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Field Type *
                </label>
                <select
                  value={currentField.type}
                  onChange={(e) => setCurrentField({...currentField, type: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3d82c4] focus:border-transparent"
                >
                  {fieldTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              {/* Placeholder */}
              {!['select', 'radio', 'checkbox', 'toggle'].includes(currentField.type) && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Placeholder Text
                  </label>
                  <input
                    type="text"
                    value={currentField.placeholder}
                    onChange={(e) => setCurrentField({...currentField, placeholder: e.target.value})}
                    placeholder="Enter placeholder text"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3d82c4] focus:border-transparent"
                  />
                </div>
              )}

              {/* Options for select, radio, checkbox */}
              {['select', 'radio', 'checkbox'].includes(currentField.type) && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Options (comma separated) *
                  </label>
                  <input
                    type="text"
                    value={currentField.options}
                    onChange={(e) => setCurrentField({...currentField, options: e.target.value})}
                    placeholder="Option 1, Option 2, Option 3"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3d82c4] focus:border-transparent"
                  />
                </div>
              )}

              {/* Help Text */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Help Text
                </label>
                <input
                  type="text"
                  value={currentField.helpText}
                  onChange={(e) => setCurrentField({...currentField, helpText: e.target.value})}
                  placeholder="Additional information for users"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3d82c4] focus:border-transparent"
                />
              </div>

              {/* Required Toggle */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="required"
                  checked={currentField.required}
                  onChange={(e) => setCurrentField({...currentField, required: e.target.checked})}
                  className="w-5 h-5 text-[#3d82c4] border-gray-300 rounded focus:ring-[#3d82c4]"
                />
                <label htmlFor="required" className="text-sm font-medium text-gray-700 cursor-pointer">
                  Required Field
                </label>
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowFieldModal(false)}
                className="px-6 py-2.5 border border-gray-300 text-gray-700 hover:bg-gray-100 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveField}
                className="px-6 py-2.5 bg-[#3d82c4] hover:bg-[#2d6fad] text-white rounded-lg font-medium transition-colors"
              >
                Save Field
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomizableForm;